import cn from 'classnames';
import { useState } from 'react';
import { CalculationTypes } from 'src/modules/newCars/constants/model';
import {
  CarSpecificationType,
  DetailedSpecificationType,
  ModelDetailType,
} from 'src/modules/newCars/types';
import { processModelSeoData } from 'src/modules/newCars/utils/seoContent';
import { Typography } from 'ui';
import { Color, TypeScales } from 'ui/primitives';
import EmiOffer from '../../../EmiOffer';
import LatestNews from '../../../LatestNews';
import { VariantsList } from '../../../VariantsList/VariantsList';
import VideoReview from '../../../VideoReview';
import Faq from '../../../content/Faq';
import ScrollableJumbo from '../../../content/ScrollableJumbo';
import { DetailedSpecifications } from '../../Variant/DetailedSpecifications/DetailedSpecifications';
import CalculatedValues from '../CalculatedValues';
import { CityVariantSelector } from '../CityVariantSelector/CityVariantSelector';
import { UnderPriceCars } from '../UnderPriceCars/UnderPriceCars';
import UserReview from '../UserReview';
import Verdict from '../Verdict';
import SpecificationDetail from './SpecificationDetail';
import s from './SpecificationPage.module.scss';
import TestDrivePopup from '../../../popups/TestDrivePopup';

const headerSpecificationData = {
  heading: '<brand-name> <model-name> Specification',
  description:
    'Introducing the impressive specifications of the <brand-name> <model-name>. With its striking design, powerful engine options, and advanced safety features, the <brand-name> <model-name> offers a remarkable driving experience. Stay connected with the intuitive infotainment system and revel in the luxurious comfort of the spacious interior. The <brand-name> <model-name> incorporates advanced technologies and innovative features for enhanced convenience and performance. Additionally, its fuel efficiency ensures economical and eco-friendly journeys. Experience the epitome of automotive excellence with the <brand-name> <model-name> as it redefines the standards of style, performance, and innovation.',
};

export const SpecificationPage = ({
  content,
  strapiContent,
  variantSpecsInModel
}: {
  content: ModelDetailType;
  strapiContent: any;
  variantSpecsInModel: Array<DetailedSpecificationType>
}) => {
  const [showTestDrive,setShowTestDrive] = useState(false);
  const [selectedCity, setSelectedCity] = useState({
    name: 'New Delhi',
    id: 4,
  });
  const [selectedVariant, setSelectedVariant] = useState(
    content?.variants?.[0]
  );
  const [variantDetailedSpecification, setVariantDetailedSpecification] = useState<any>(variantSpecsInModel);

  const specification: CarSpecificationType = {
    mileage: selectedVariant?.mileage,
    engine: selectedVariant?.engine,
    transmission: selectedVariant?.transmission,
    fuelType: selectedVariant?.fuelType,
    seatingCapacity: selectedVariant?.seatingCapacity ?? "N/A",
  };

  return (
    <div className={s.root}>
      <div className={s.top}>
        <div className={cn(s.city_selector)}>
          <CityVariantSelector
            setVariantDetailedSpecification={setVariantDetailedSpecification}
            content={content}
            strapiContent={strapiContent}
            selectedCity={selectedCity}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            setSelectedCity={setSelectedCity}
          />
        </div>
        <div className={s.spec_emi}>
          <SpecificationDetail
            specification={specification}
            price={selectedVariant?.price}
          />
          <EmiOffer price={selectedVariant?.priceInNumbers} handleGetEmiOffers={()=>setShowTestDrive(true)}/>
        </div>
      </div>
      <div className={s.header_data}>
        <Typography
          className={s.heading}
          label={processModelSeoData(headerSpecificationData.heading, content)}
          variant={TypeScales.h1}
          renderAs="H1"
        />
        <Typography
          label={processModelSeoData(
            headerSpecificationData.description,
            content
          )}
          variant={TypeScales.base_big_r}
          color={Color.text_subheading}
        />
      </div>
      <DetailedSpecifications
        detail={variantDetailedSpecification}
        label={`Key specifications of ${content?.hero?.brandName} ${content?.hero?.modelName}`}
      />
      <UnderPriceCars
          content={content}
      />
      <VariantsList
        variants={content?.variants}
        modelName={content?.hero?.modelName}
        modelImage={content?.hero?.image}
        brandSlug={content?.modelSeoData?.brandSlug}
        modelSlug={content?.modelSeoData?.modelSlug}
      />
      {!content?.hero?.fuelType.includes("ev") && <div className={s.calc_values}>
        <CalculatedValues
          type={CalculationTypes.runningCost}
          variantList={content?.variants}
          brandName={content?.hero?.brandName}
          modelName={content?.hero?.modelName}
        />
      </div>}

      {strapiContent?.news?.length && (
        <LatestNews
          data={strapiContent?.news}
          heading={`${content?.hero?.brandName} ${content?.hero?.modelName} latest news`}
        />
      )}
      {strapiContent?.userReview && (
        <UserReview
          link={`/new-cars/${content?.modelSeoData?.brandSlug}-car/${content?.modelSeoData?.modelSlug}/review`}
          ratingData={strapiContent.userReview}
          calculatedReview={strapiContent.calculatedReview}
        />
      )}
      {strapiContent?.videoReview && (
        <VideoReview data={strapiContent.videoReview} />
      )}
      {strapiContent?.carListing?.mustRead && (
        <ScrollableJumbo
          heading="Must read article"
          description={strapiContent?.carListing?.mustRead}
        />
      )}
      {strapiContent?.verdict && (
        <Verdict verdictData={strapiContent.verdict} />
      )}
      {strapiContent?.carListing?.faqTitle &&
        strapiContent?.carListing?.faqContent && (
          <Faq
            faqTitle={processModelSeoData(
              strapiContent?.carListing?.faqTitle,
              content
            )}
            faqContent={processModelSeoData(
              strapiContent?.carListing?.faqContent,
              content
            )}
          />
        )}
        {showTestDrive && <TestDrivePopup modelId={content?.modelId} onClose={() => setShowTestDrive(false)} show={showTestDrive}/>}
    </div>
  );
};
